import React, { useMemo, useEffect } from 'react';
import {
  PhonicsLesson,
  useGetPhonicsLessonGroupsWithLessonsQuery,
} from '@/api/types';
import { useLessonPlayer } from '@/context/LessonPlayer';
import { getLetterSetsBySounds } from '@/utils/letterSet';
import { getLessonsBySymbols } from '@/utils/lessons';
import { PhonicsLogo } from '@/components';
import { Character, RewardBadge } from '@/components/lessonKit';
import {
  ManageDecodableBookPod,
  ManageDecodableBooksNone,
} from '@/components/manage';
import { ActivityStepSkeleton } from '../Activity';

interface LessonFinishedStepProps {
  lesson: PhonicsLesson;
}

export const LessonFinishedStep = ({ lesson }: LessonFinishedStepProps) => {
  const { registerSlide } = useLessonPlayer();

  const { data: groupsResult } = useGetPhonicsLessonGroupsWithLessonsQuery();

  const letterSets = useMemo(
    () =>
      lesson.sounds
        ? getLetterSetsBySounds(lesson.sounds.map((sound) => sound.name))
        : undefined,
    [lesson],
  );

  const lessons = useMemo(() => {
    if (!groupsResult?.getPhonicsLessonGroups) {
      return [];
    }

    return getLessonsBySymbols(
      { sounds: letterSets?.flat() },
      groupsResult?.getPhonicsLessonGroups,
    );
  }, [groupsResult, letterSets]);

  const bookIds = useMemo(
    () => lessons.flatMap((lesson) => lesson.decodableBookIds || []),
    [lessons],
  );

  useEffect(() => {
    registerSlide('lesson-finished');
  }, [registerSlide]);

  return (
    <ActivityStepSkeleton identifier={`lesson-finished`} stage="action">
      <div className="flex flex-col md:flex-row gap-24 h-full justify-center items-center">
        <div className="flex justify-center w-full md:w-1/3">
          <Character pose="trophy" width="min(30vw,360px)" animate="none" />
        </div>

        <div className="flex flex-col gap-8 w-full md:w-1/3">
          <RewardBadge>NEW BOOK UNLOCKED</RewardBadge>

          <h2
            className="text-white text-5xl uppercase tracking-wide leading-none"
            style={{ fontFamily: 'var(--lp-display), Impact, sans-serif' }}
          >
            Lesson complete!
          </h2>

          <PhonicsLogo className="w-48 h-auto" />

          {bookIds ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {bookIds?.map((bookId) => (
                <ManageDecodableBookPod key={bookId} bookId={bookId} />
              ))}
            </div>
          ) : (
            <ManageDecodableBooksNone />
          )}
        </div>
      </div>
    </ActivityStepSkeleton>
  );
};
